
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';

import { BsModalService } from 'ngx-bootstrap/modal';

import { Offer } from '../../../model/offer';
import { SharedService } from '../../../_services/shared.service';
import { UiNewOfferPhotoGalleryComponent } from '../ui-new-offer-photo-gallery/ui-new-offer-photo-gallery.component';
import { LxResponsiveClassDirective } from '../../../directives/lx-responsive-class.directive';

@Component({
  selector: 'ui-new-offer-image',
  standalone: true,
  imports: [NgOptimizedImage, LxResponsiveClassDirective],
  templateUrl: './ui-new-offer-image.component.html',
  styleUrls: ['./ui-new-offer-image.component.scss'],
})
export class UiNewOfferImageComponent implements OnInit, OnChanges {
  readonly arrowResponsiveClass = {
    horizontal: 'carousel-btn-visible',
    vertical: 'carousel-btn-hidden',
  } as const;

  @Input() offer!: Offer | null;
  @Input() index = 0;
  @Input() propertyId = '';
  @Input() offerAliasId = '';
  @Input() viewMode: 'tiles' | 'list' = 'tiles';
  currentImageIndex = 0;
  totalImages = 0;
  private pointerStartX: number | null = null;
  private pointerStartY: number | null = null;
  private isPointerDragging = false;
  private readonly swipeThresholdPx = 40;

  constructor(
    private modalService: BsModalService,
    public sharedService: SharedService,
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['offer']) {
      const urls = this.offer?.amenities?.room_photo_urls || [];
      this.totalImages = urls.length > 0 ? urls.length : (this.offer?.amenities?.room_photo_url ? 1 : 0);
      if (this.currentImageIndex >= this.totalImages) {
        this.currentImageIndex = 0;
      }
      this.preloadNeighborImages();
    }
  }

  /**
   * Preloads the images adjacent to the current index so that a left/right
   * swipe can display them from cache instead of waiting on a network fetch.
   * Only the previous and next images are prefetched to keep the number of
   * concurrent requests low when many offer cards are on screen at once.
   */
  private preloadNeighborImages(): void {
    const urls = this.offer?.amenities?.room_photo_urls || [];
    if (urls.length <= 1) {
      return;
    }

    const nextIndex = (this.currentImageIndex + 1) % urls.length;
    const prevIndex = (this.currentImageIndex - 1 + urls.length) % urls.length;

    for (const index of new Set([nextIndex, prevIndex])) {
      const url = urls[index];
      if (!url) {
        continue;
      }
      const img = new Image();
      img.decoding = 'async';
      img.src = encodeURI(url);
    }
  }

  getDisplayImageUrl(): string {
    const amen = this.offer?.amenities;
    if (!amen) {
      return 'none';
    }

    const urls = amen.room_photo_urls || [];
    // If an array of photos exists, use the currentImageIndex if valid
    if (urls.length > 0) {
      this.totalImages = urls.length;
      // normalize current index
      if (this.currentImageIndex >= this.totalImages) {
        this.currentImageIndex = 0;
      }
      return encodeURI(urls[this.currentImageIndex]);
    }

    // Fallback to single photo url
    const url = amen.room_photo_url;
    if (url) {
      this.totalImages = 1;
      this.currentImageIndex = 0;
      return encodeURI(url);
    }

    this.totalImages = 0;
    return 'none';
  }

  hasImage(): boolean {
    return this.getDisplayImageUrl() !== 'none';
  }

  openModal(): void {
    if (!this.offer) {
      return;
    }

    this.sharedService.setSelectedOfferByOffer(this.index, this.offer, false);

    this.modalService.show(UiNewOfferPhotoGalleryComponent, {
      ...this.sharedService.config,
      initialState: {
        offer: this.sharedService.offer,
        index: this.index,
        propertyId: this.propertyId,
        offerAliasId: this.offerAliasId,
      },
    });
  }

  // Image counter helpers
  nextImage(ev?: Event): void {
    if (ev) {
      ev.stopPropagation();
    }
    if (!this.offer?.amenities?.room_photo_urls?.length) {
      return;
    }
    this.currentImageIndex = (this.currentImageIndex + 1) % this.totalImages;
    this.preloadNeighborImages();
  }

  prevImage(ev?: Event): void {
    if (ev) {
      ev.stopPropagation();
    }
    if (!this.offer?.amenities?.room_photo_urls?.length) {
      return;
    }
    this.currentImageIndex = (this.currentImageIndex - 1 + this.totalImages) % this.totalImages;
    this.preloadNeighborImages();
  }

  goToImage(index: number, ev?: Event): void {
    if (ev) {
      ev.stopPropagation();
    }
    if (!this.offer?.amenities?.room_photo_urls?.length) {
      return;
    }
    if (index < 0 || index >= this.totalImages) {
      return;
    }
    this.currentImageIndex = index;
    this.preloadNeighborImages();
  }

  onPointerDown(event: PointerEvent): void {
    if (this.totalImages <= 1) {
      return;
    }

    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.isPointerDragging = true;
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.isPointerDragging || this.pointerStartX == null || this.pointerStartY == null) {
      this.resetPointerGesture();
      return;
    }

    const deltaX = event.clientX - this.pointerStartX;
    const deltaY = event.clientY - this.pointerStartY;

    if (Math.abs(deltaX) >= this.swipeThresholdPx && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.stopPropagation();

      if (deltaX < 0) {
        this.nextImage(event);
      } else {
        this.prevImage(event);
      }
    }

    this.resetPointerGesture();
  }

  onPointerCancel(): void {
    this.resetPointerGesture();
  }

  private resetPointerGesture(): void {
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.isPointerDragging = false;
  }
}
